/**
 * Sidebar Navigation — single source of truth
 *
 * Every sidebar page is declared here once, with the module key that gates it.
 * Consumed by:
 *   - Layout.js               — renders the sidebar from these lists (icons are
 *                               attached there via path-keyed icon maps)
 *   - PermissionsDialog.js    — derives the "pages covered" caption under each
 *                               Web Section Access checkbox via pagesForModule()
 *   - CompanyModulesDialog.js — same captions for per-company module whitelist
 *
 * Adding a new sidebar page = ONE entry in the right list below (+ an icon in
 * Layout.js's icon map, otherwise it gets a sensible default icon). The
 * permissions dialogs pick it up automatically.
 *
 * Item shape: { text, path, moduleKey, adminOnly?, feature? }
 *   adminOnly: true       — only rendered for users with role === 'admin'
 *   feature: 'experts'    — only rendered when REACT_APP_FEATURE_EXPERTS !== 'false'
 */

import { MODULES, IN_APP_CHAT_ENABLED } from './modules';

// Top-level menu (no submenu)
export const CORE_NAV_ITEMS = [
  { text: 'Dashboard', path: '/', moduleKey: MODULES.DASHBOARD },
  { text: 'Action Queue', path: '/operations', moduleKey: MODULES.DASHBOARD },
  { text: 'Brands', path: '/brands', moduleKey: MODULES.BRANDS },
  { text: 'Connectors', path: '/connectors', moduleKey: MODULES.CONNECTORS },
  ...(IN_APP_CHAT_ENABLED ? [{ text: 'Chat', path: '/chat', moduleKey: MODULES.CHAT }] : []),
  { text: 'AI Agents', path: '/experts', moduleKey: MODULES.EXPERTS, feature: 'experts' },
  { text: 'Scheduled Jobs', path: '/scheduled-jobs', moduleKey: MODULES.SCHEDULED_JOBS },
  { text: 'Call Centre', path: '/call-centre', moduleKey: MODULES.CALL_CENTRE },
  { text: 'Contacts', path: '/contacts', moduleKey: MODULES.DASHBOARD },
  { text: 'Admin Panel', path: '/admin', moduleKey: MODULES.ADMIN, adminOnly: true },
  { text: 'Audit Log', path: '/admin/audit-log', moduleKey: MODULES.ADMIN, adminOnly: true },
];

// Inventory submenu
export const INVENTORY_NAV_ITEMS = [
  { text: 'Overview', path: '/inventory', moduleKey: MODULES.INVENTORY },
  { text: 'Intelligence', path: '/inventory/summary', moduleKey: MODULES.INVENTORY },
  { text: 'Stock Levels', path: '/inventory/stock', moduleKey: MODULES.INVENTORY_STOCK },
  { text: 'Batches', path: '/inventory/batches', moduleKey: MODULES.INVENTORY_BATCHES },
  { text: 'Movements', path: '/inventory/movements', moduleKey: MODULES.INVENTORY_STOCK },
  { text: 'MSR', path: '/reports/run-rates', moduleKey: MODULES.INVENTORY_REPORTS },
  { text: 'Products & COGS', path: '/inventory/cogs', moduleKey: MODULES.INVENTORY_COGS },
  { text: 'BOM Catalog', path: '/inventory/boms', moduleKey: MODULES.INVENTORY_COGS },
  { text: 'Categories', path: '/inventory/categories', moduleKey: MODULES.INVENTORY_COGS },
  { text: 'Packaging', path: '/inventory/packaging', moduleKey: MODULES.INVENTORY_PACKAGING },
  { text: 'Work Orders', path: '/inventory/work-orders', moduleKey: MODULES.INVENTORY_COGS },
  { text: 'CIN7 Unresolved', path: '/inventory/cin7-unresolved', moduleKey: MODULES.INVENTORY_COGS },
  { text: 'Commercial Invoices', path: '/inventory/commercial-invoices', moduleKey: MODULES.INVENTORY_TRANSFERS },
  { text: 'Warehouses', path: '/inventory/warehouses', moduleKey: MODULES.INVENTORY_WAREHOUSES },
  { text: 'WH → Brand → Subsidiary', path: '/inventory/warehouse-brand-subsidiary', moduleKey: MODULES.INVENTORY_WAREHOUSES },
  { text: 'Store Mappings', path: '/inventory/mappings', moduleKey: MODULES.INVENTORY_MAPPINGS },
  { text: 'Transfers', path: '/inventory/transfers', moduleKey: MODULES.INVENTORY_TRANSFERS },
  { text: 'Returns', path: '/inventory/returns', moduleKey: MODULES.INVENTORY_RETURNS },
  { text: 'Disposals', path: '/inventory/disposals', moduleKey: MODULES.INVENTORY_DISPOSALS },
  { text: 'Physical Counts', path: '/inventory/counts', moduleKey: MODULES.INVENTORY_COUNTS },
  { text: 'Trust Point Wizard', path: '/inventory/trust-point-wizard', moduleKey: MODULES.INVENTORY_COUNTS },
  { text: 'FBA Discrepancies', path: '/inventory/fba-discrepancies', moduleKey: MODULES.INVENTORY_FBA },
  { text: 'Amazon Restock', path: '/inventory/amazon-restock', moduleKey: MODULES.INVENTORY_FBA },
  { text: 'FBA Capacity Limits', path: '/inventory/fba-capacity', moduleKey: MODULES.INVENTORY_FBA },
];

// For shopify inventory mode, only show simplified menu (no warehouse management)
export const SHOPIFY_INVENTORY_VISIBLE_PATHS = ['/inventory', '/inventory/summary', '/inventory/stock', '/inventory/cogs'];

// Procurement submenu
export const PROCUREMENT_NAV_ITEMS = [
  { text: 'Overview', path: '/procurement', moduleKey: MODULES.PROCUREMENT },
  { text: 'Suppliers', path: '/procurement/suppliers', moduleKey: MODULES.PROCUREMENT_SUPPLIERS },
  { text: 'Purchase Orders', path: '/procurement/purchase-orders', moduleKey: MODULES.PROCUREMENT_PO },
  { text: 'Goods Receipts', path: '/procurement/goods-receipts', moduleKey: MODULES.PROCUREMENT_RECEIPTS },
  { text: 'Imported Receipts', path: '/procurement/imported-receipts', moduleKey: MODULES.PROCUREMENT_RECEIPTS },
  { text: 'Invoices', path: '/procurement/invoices', moduleKey: MODULES.PROCUREMENT_INVOICES },
  { text: 'Payments', path: '/procurement/payments', moduleKey: MODULES.PROCUREMENT_PAYMENTS },
];

// Retail submenu — DTC + wholesale (sales-side)
export const RETAIL_NAV_ITEMS = [
  { text: 'Overview', path: '/retail', moduleKey: MODULES.RETAIL },
  { text: 'Orders', path: '/retail/orders', moduleKey: MODULES.RETAIL_ORDERS },
  { text: 'Price Tiers', path: '/inventory/price-tiers', moduleKey: MODULES.RETAIL_ORDERS },
  { text: 'Customers', path: '/retail/customers', moduleKey: MODULES.RETAIL_ORDERS },
  // "New Wholesale Order" sidebar entry removed — the "+ Add New Order"
  // button inside /retail/orders is the single entry point now.
  { text: 'Pending Ship', path: '/wholesale/pending-ship', moduleKey: MODULES.RETAIL_ORDERS },
  { text: 'Ready to Invoice', path: '/wholesale/ready-to-invoice', moduleKey: MODULES.RETAIL_ORDERS },
  { text: 'Bank Reconciliation', path: '/retail/bank-reconciliation', moduleKey: MODULES.RETAIL_ORDERS },
];

// Finance section — direct entries (own accounting system), rendered above the
// QuickBooks subsection. Order here = order in the sidebar.
export const FINANCE_DIRECT_NAV_ITEMS = [
  { text: 'Journal Reconciliation', path: '/finance/journal-reconciliation', moduleKey: MODULES.FINANCE_RECONCILIATION },
  // 'Financial Statements' (/finance/statements) is deliberately hidden from the sidebar —
  // its figures are not yet fully accurate. The route still works and is reachable via a
  // discreet link inside the Reconciliation Hub (ReconciliationHub.js) so it isn't surfaced
  // broadly. Re-add this entry once the statements numbers are trusted.
  { text: 'Bank GL Mappings', path: '/finance/bank-gl-mappings', moduleKey: MODULES.FINANCE_SETTINGS },
  { text: 'Subsidiaries', path: '/subsidiaries', moduleKey: MODULES.FINANCE_SETTINGS },
  { text: 'P&L Report', path: '/finance/pl', moduleKey: MODULES.FINANCE_PL },
  { text: 'Balance Sheet', path: '/finance/balance-sheet', moduleKey: MODULES.FINANCE_PL },
  { text: 'Cash Flow', path: '/finance/cash-flow', moduleKey: MODULES.FINANCE_PL },
  { text: 'Cash Flow Projection', path: '/finance/cash-flow-projections', moduleKey: MODULES.FINANCE_PL },
  { text: 'Processor Balances', path: '/finance/processor-balances', moduleKey: MODULES.FINANCE_PL },
  { text: 'Tax Returns', path: '/finance/tax-returns', moduleKey: MODULES.FINANCE_SETTINGS },
  { text: 'VAT Vendors', path: '/finance/vat-vendors', moduleKey: MODULES.FINANCE_SETTINGS },
  { text: 'Search', path: '/finance/search', moduleKey: MODULES.FINANCE_RECONCILIATION },
  { text: 'Periods', path: '/finance/periods', moduleKey: MODULES.FINANCE_SETTINGS },
  { text: 'Data Sources', path: '/finance/data-sources', moduleKey: MODULES.FINANCE_SETTINGS },
];

// QuickBooks submenu (nested under Finance) — all QB items EXCEPT P&L Report
export const FINANCE_QB_NAV_ITEMS = [
  { text: 'Overview', path: '/finance', moduleKey: MODULES.FINANCE },
  { text: 'Cash Position', path: '/finance/cash', moduleKey: MODULES.FINANCE_CASH },
  { text: 'AR Analysis', path: '/finance/ar', moduleKey: MODULES.FINANCE_AR },
  { text: 'Credit Notes', path: '/finance/credit-notes', moduleKey: MODULES.FINANCE_AR },
  { text: 'AP Analysis', path: '/finance/ap', moduleKey: MODULES.FINANCE_AP },
  { text: 'Goods in Transit', path: '/finance/goods-in-transit', moduleKey: MODULES.FINANCE_AP },
  { text: 'Intercompany', path: '/finance/intercompany', moduleKey: MODULES.FINANCE_INTERCOMPANY },
  { text: 'Reconciliation', path: '/finance/reconciliation', moduleKey: MODULES.FINANCE_RECONCILIATION },
  { text: 'Settings', path: '/finance/settings', moduleKey: MODULES.FINANCE_SETTINGS },
];

export const ALL_NAV_ITEMS = [
  ...CORE_NAV_ITEMS,
  ...INVENTORY_NAV_ITEMS,
  ...PROCUREMENT_NAV_ITEMS,
  ...RETAIL_NAV_ITEMS,
  ...FINANCE_DIRECT_NAV_ITEMS,
  ...FINANCE_QB_NAV_ITEMS,
];

/**
 * All sidebar page names gated by a module key, in sidebar order.
 * Used by the permission dialogs to show what each checkbox covers.
 */
export function pagesForModule(moduleKey) {
  return ALL_NAV_ITEMS.filter(item => item.moduleKey === moduleKey).map(item => item.text);
}
